import { useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../hooks/useAsync";
import { useCloud } from "../context/CloudContext";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import StatCard from "../components/StatCard";
import { Loading, ErrorState } from "../components/States";
import LineChart from "../components/charts/LineChart";
import StackedBars from "../components/charts/StackedBars";
import {
  currencyCompact,
  currencyExact,
  number,
  quantity as qty,
  titleCase,
} from "../lib/format";
import { buildPivot, pivotCsv, downloadCsv } from "../lib/pivot";
import { monthlyCharts } from "../lib/monthly";
import {
  monthTick,
  monthFull,
  dayTick,
  dayFull,
  dayRange,
} from "../lib/timeAxis";
import { bandwidthSummary, bandwidthText } from "../lib/bandwidth";
import { toId, sameId } from "../lib/ids";
import { firstOfThisUTCMonth, formatUTCDate, todayUTC } from "../lib/utcDate";

// The usage report identifies a tenancy by OCID; the backend resolves the
// display name from the organization's tenancy list, which can come back empty
// for a standalone tenancy. A row without a name still says which tenancy it
// came from: show the tail of the OCID — the part that differs between them —
// with the full id on hover.
const tenantTail = (id) => (id ? `…${id.slice(-12)}` : "—");

// Unit rates are fractions of a cent, so they need more precision than the
// 2-decimal currency format used for amounts.
const rateText = (v) =>
  (v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 6 });

const fmtDate = formatUTCDate;

// How much history the charts on this page ask for — the spend trend and the data
// transfer series, both "the last year" and neither scoped by the window picker.
const TREND_MONTHS = 12;

// The span allowed for the client's stored monthly snapshot, and the default.
//
// Deliberately not TREND_MONTHS any more. The chart above is a year of context for
// the operator; how much history the *client* gets is a separate decision, because
// a relationship may be three months old or three years.
//
// The ceiling matches services.MaxSnapshotMonths — the backend clamps anything
// higher, so accepting more here would promise history the sync will not return.
// The floor is 1, the current month on its own, which is the smallest snapshot that
// means anything.
const MIN_SNAPSHOT_MONTHS = 1;
const MAX_SNAPSHOT_MONTHS = 36;
const DEFAULT_SNAPSHOT_MONTHS = 12;

// Clamp a typed span into range. An unreadable or empty field falls back to the
// default rather than to zero — zero would reach the backend as "use the default"
// anyway, and silently storing a year when the box looks empty is worse than
// storing one the operator can see.
const snapshotSpan = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_SNAPSHOT_MONTHS;
  return Math.min(Math.max(n, MIN_SNAPSHOT_MONTHS), MAX_SNAPSHOT_MONTHS);
};

export default function Costs() {
  const { activeProvider, providers } = useCloud();
  // Lazy initialisers so the default UTC window is "today" when the page mounts.
  const [start, setStart] = useState(firstOfThisUTCMonth);
  const [end, setEnd] = useState(todayUTC);
  const [groupId, setGroupId] = useState("");
  const [query, setQuery] = useState("");
  // What the date × service · SKU pivot shows, and therefore what it exports.
  const [metric, setMetric] = useState("cost"); // 'cost' | 'usage'
  // How the monthly trend is read: the per-service breakdown, the total line, or the
  // same figures as a table — which is also how every value stays reachable without
  // a pointer.
  //
  // The breakdown leads. A total line says only whether spend is rising; the
  // per-service lines say that *and* what is driving it, since their shape sums to
  // the total. The total view stays for the months where that is the whole question.
  const [trendView, setTrendView] = useState("service"); // 'service' | 'total' | 'table'
  // Which bucket the spend chart plots. Orthogonal to the view above — all three views
  // work at either grain — because they answer different questions: the grain decides
  // how finely time is cut, the view decides what is drawn per cut.
  //
  // Month leads. A year of context is what the page opens on, and a day series only
  // means something once someone has a window in mind; asking for one costs a live OCI
  // query, which is why it is not fetched until this is switched.
  const [trendGrain, setTrendGrain] = useState("month"); // 'month' | 'day'
  // Data transfer as lines or as figures — the table is also how the values stay
  // reachable without a pointer.
  const [transferView, setTransferView] = useState("flow"); // 'flow' | 'sku' | 'table'
  const [usageProvider, setUsageProvider] = useState("oci");
  // Null until the user clicks "Get usage" — so nothing loads on page open.
  const [applied, setApplied] = useState(null);
  // Storing the pulled window for client access.
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  // Storing the last twelve months for the client's own spend chart. Its own state,
  // not shared with the window sync: the two write different tables and either can
  // fail on its own, so one result message must not stand in for the other.
  const [monthsSyncing, setMonthsSyncing] = useState(false);
  const [monthsResult, setMonthsResult] = useState(null);
  const [monthsError, setMonthsError] = useState(null);
  // How many calendar months the next "Store for client" writes. Its own state
  // rather than part of `applied`: it changes nothing on this page, so changing it
  // must not invalidate the report or re-query the provider.
  //
  // Held as the raw string the field contains, not as a number, so it can be empty
  // or half-typed while someone edits it. Clamping on every keystroke would make
  // "36" impossible to type from "3" -> "36" the moment a digit took it out of
  // range; it is clamped on blur and again before the request instead.
  const [snapshotMonths, setSnapshotMonths] = useState(
    String(DEFAULT_SNAPSHOT_MONTHS),
  );

  const { data, loading, error, reload } = useAsync(
    () => api.getCosts(activeProvider),
    [activeProvider],
  );
  const groupsReq = useAsync(() => api.getUserGroups(), []);
  const groups = groupsReq.data || [];
  const usage = useAsync(
    () =>
      applied
        ? api.getUsageReport(applied.provider, {
            start: applied.start,
            end: applied.end,
            byDate: true,
            tenant: applied.tenant,
            // Applies the group's price overrides to the rows; without it the
            // report is the provider's own rates only.
            groupId: applied.groupId,
          })
        : Promise.resolve(null),
    [applied],
  );
  // The twelve-month trend is not scoped by the date range — it is always the last
  // year — so it is keyed on the provider and the tenancy alone. Re-running "Get
  // usage" over a different window does not re-query a year of monthly cost.
  const trend = useAsync(
    () =>
      applied
        ? api.getUsageTrend(applied.provider, {
            months: TREND_MONTHS,
            tenant: applied.tenant,
          })
        : Promise.resolve(null),
    [applied?.provider, applied?.tenant],
  );
  // The day series covers the *window*, so unlike the trend above it is keyed on the
  // dates too. It is also keyed on the grain and resolves null until Day is selected:
  // this is a live OCI query, and an operator who never touches the toggle must not
  // spend one. Switching back to Month keeps both cached — the two are separate
  // useAsync calls, so neither refetches on a toggle.
  const dailyTrend = useAsync(
    () =>
      applied && trendGrain === "day"
        ? api.getUsageDaily(applied.provider, {
            start: applied.start,
            end: applied.end,
            tenant: applied.tenant,
          })
        : Promise.resolve(null),
    [applied, trendGrain],
  );
  // Data transfer is a separate read, and has to be: the report and the trend above
  // are cost queries, and OCI does not charge for ingress — so inbound traffic exists
  // in no cost response at any granularity. Keyed like the trend, on provider and
  // tenancy rather than on the date range.
  const transfer = useAsync(
    () =>
      applied
        ? api.getTransferTrend(applied.provider, {
            months: TREND_MONTHS,
            tenant: applied.tenant,
          })
        : Promise.resolve(null),
    [applied?.provider, applied?.tenant],
  );
  // Contracts for the applied group drive the summary box (value / % used).
  const groupDetail = useAsync(
    () =>
      applied?.groupId
        ? api.getUserGroup(applied.groupId)
        : Promise.resolve(null),
    [applied],
  );

  const applyUsage = () => {
    const g = groups.find((x) => sameId(x.id, groupId));
    setApplied({
      provider: usageProvider,
      start,
      end,
      // Filter by the tenancy OCID where the group has one: display names are
      // not unique, so a name could pull in another tenancy's usage. The name
      // is kept alongside it purely to label the report.
      tenant: g?.tenancyId || g?.tenancyName || "",
      tenantLabel: g?.tenancyName || "",
      groupId,
    });
  };

  // Persist the pulled window so the group's own members can read it on the
  // Billing page — they have no access to the provider API.
  const storeUsage = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const res = await api.syncUsage({
        providerId: applied.provider,
        groupId: toId(applied.groupId),
        start: applied.start,
        end: applied.end,
      });
      setSyncResult(res);
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  // Persist a run of calendar months for the group, so their own Billing page can
  // draw the spend chart. Independent of the window above: this stores calendar
  // months at SKU grain, which is what lets the client's chart be repriced at read
  // time.
  //
  // The span is the operator's, and the write REPLACES the group's whole snapshot —
  // storing three months after twelve leaves three, it does not top them up. That is
  // the same replace semantics a re-sync has always had, and it is what keeps the
  // client's axis honest about how far the snapshot reaches.
  const storeMonths = async () => {
    setMonthsSyncing(true);
    setMonthsResult(null);
    setMonthsError(null);
    try {
      const res = await api.syncUsageMonths({
        providerId: applied.provider,
        groupId: toId(applied.groupId),
        months: snapshotSpan(snapshotMonths),
      });
      setMonthsResult(res);
    } catch (err) {
      setMonthsError(err.message);
    } finally {
      setMonthsSyncing(false);
    }
  };

  if (loading) return <Loading label="Loading cost data…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const scopeName =
    providers.find((p) => p.id === activeProvider)?.shortName ||
    "All providers";

  const hasSearched = !!applied;
  // per-date rows (byDate=true, tenant-filtered by backend), plus meta
  const rawRows = usage.data?.data || [];
  const usageMeta = usage.data?.meta || {};
  // Ends in a literal, not in whatever the last source happened to hold: the report's
  // first row carries no currency when OCI omitted it, and /costs reports none at all
  // when there is no spend to report. This value is a *label* as much as a format
  // argument — it names the per-currency summary cards — so an empty one would read as
  // "Usage cost ()" even where nothing threw.
  const usageCurrency = rawRows[0]?.currency || data.currency || "USD";

  // Keep one display row per OCI usage line. OCI returns the final cost, not the
  // provider's internal tier allocation, so the report uses that cost directly.
  const aggMap = new Map();
  for (const r of rawRows) {
    const k = JSON.stringify([
      r.tenantId || r.tenantName,
      r.service,
      r.skuId,
      r.currency,
      r.unit,
    ]);
    const e = aggMap.get(k);
    if (e) {
      e.cost += r.cost || 0;
      e.quantity += r.quantity || 0;
      e.billing += r.billing || 0;
    } else {
      aggMap.set(k, {
        ...r,
        date: undefined,
        cost: r.cost || 0,
        quantity: r.quantity || 0,
        billing: r.billing || 0,
      });
    }
  }
  const q = query.trim().toLowerCase();
  const filtered = [...aggMap.values()]
    .filter((r) => {
      if (
        q &&
        !`${r.tenantName} ${r.service} ${r.skuId} ${r.skuName}`
          .toLowerCase()
          .includes(q)
      )
        return false;
      return true;
    })
    .sort(
      (a, b) =>
        (a.tenantId || a.tenantName).localeCompare(
          b.tenantId || b.tenantName,
        ) ||
        (a.service || "").localeCompare(b.service || "") ||
        (a.skuId || "").localeCompare(b.skuId || "") ||
        (a.currency || "").localeCompare(b.currency || ""),
    );
  const tenantCount = new Set(filtered.map((r) => r.tenantName)).size;
  const serviceCount = new Set(filtered.map((r) => r.service)).size;

  // Data transfer in and out, picked out of the same rows the table shows — so the
  // Filter box narrows it along with everything else. Per unit, never summed across
  // units, and empty rather than zero where the provider reported no such line.
  const bandwidth = bandwidthSummary(filtered);

  // Money is only ever added up within one currency. A report can carry rows in
  // more than one — OCI reports some SKUs in the tenancy's currency — and a
  // single "Usage cost" that added USD to SGD would be a figure that means
  // nothing. The summary therefore carries one card per currency present, the
  // same rule the pivot's Total columns follow.
  const totalsByCurrency = {};
  for (const r of filtered) {
    const c = r.currency || usageCurrency;
    const t = (totalsByCurrency[c] = totalsByCurrency[c] || {
      cost: 0,
      billing: 0,
      lines: 0,
      priced: 0,
    });
    t.cost += r.cost || 0;
    t.billing += r.billing || 0;
    t.lines += 1;
    if (r.priced) t.priced += 1;
  }
  const totalsFor = (c) =>
    totalsByCurrency[c] || { cost: 0, billing: 0, lines: 0, priced: 0 };
  // An empty report still shows one (zeroed) card rather than none.
  const summaryCurrencies = Object.keys(totalsByCurrency).sort();
  if (summaryCurrencies.length === 0) summaryCurrencies.push(usageCurrency);
  const multiCurrency = summaryCurrencies.length > 1;
  // A single-currency report keeps reading "Usage cost", not "Usage cost (USD)".
  const perCurrency = (label, c) => (multiCurrency ? `${label} (${c})` : label);
  const marginPct = (c) => {
    const t = totalsFor(c);
    return t.billing
      ? `${(((t.billing - t.cost) / t.billing) * 100).toFixed(1)}%`
      : "—";
  };

  // When a group is in scope, billing mirrors OCI cost. OCI does not return its
  // tier allocation, so the UI must not invent tier prices for display.
  const hasBilling = !!applied?.groupId;

  // Contract summary for the selected group. A contract is held in one currency,
  // so it is measured against the usage in *that* currency only — spend in
  // another currency is not convertible here and would inflate "% used".
  const contracts = groupDetail.data?.contracts || [];
  const contractValue = contracts.reduce((s, c) => s + (c.value || 0), 0);
  const contractCurrency = contracts[0]?.currency || usageCurrency;
  const contractUsage = totalsFor(contractCurrency).cost;
  const pctUsed =
    contractValue > 0 ? (contractUsage / contractValue) * 100 : null;
  const remainder = contractValue - contractUsage;

  // The twelve-month trend arrives as one row per (month, service, currency), which
  // is enough for all three views below: the services of a month sum back to that
  // month's total exactly, so the total line needs no second query.
  const trendRows = trend.data?.data || [];
  const trendMeta = trend.data?.meta || {};
  // The axis is the backend's, not the browser's: it lists exactly the months that
  // were queried, so a clock already into the next month cannot shift every point
  // by one.
  const trendAxis = trendMeta.axis || [];
  const trendCharts = monthlyCharts(trendRows, {
    axis: trendAxis,
    splits: trendMeta.currencies,
    valueOf: (r) => r.cost,
    fallbackCurrency: usageCurrency,
  });

  // The day series is the same shape one bucket finer, from its own endpoint. It keys
  // on `date` rather than `month`, which is the only thing monthlyCharts needs told:
  // the ranking, the four-colour fold and the gap rule are grain-agnostic.
  const dailyRows = dailyTrend.data?.data || [];
  const dailyMeta = dailyTrend.data?.meta || {};
  const dailyAxis = dailyMeta.axis || [];
  const dailyCharts = monthlyCharts(dailyRows, {
    axis: dailyAxis,
    splits: dailyMeta.currencies,
    valueOf: (r) => r.cost,
    bucketOf: (r) => r.date,
    fallbackCurrency: usageCurrency,
  });

  // Everything the spend card renders, resolved once per grain, so the three views
  // below are written once rather than twice. The request is part of it: the two grains
  // are separate fetches with separate loading and error states, and the card has to
  // show whichever one it is currently drawing.
  const byDay = trendGrain === "day";
  const spend = byDay
    ? {
        req: dailyTrend,
        charts: dailyCharts,
        axis: dailyAxis,
        formatLabel: dayTick,
        formatPoint: dayFull,
        // "Daily spend by service — 1 – 8 Aug 2026": the span is the window that was
        // queried, and naming it is what keeps the chart from reading as a re-cut of
        // the twelve months. The applied dates, not the axis, so the title is right
        // while the query is still in flight and after a cap trimmed the axis — the
        // hint below is where a trim gets said.
        span: applied ? dayRange(applied.start, applied.end) : "",
        // The bucket named two ways, because English needs both: "per day" and "daily
        // spend". Carried rather than derived — "dayly" is what deriving one from the
        // other produces.
        noun: "day",
        adjective: "daily",
      }
    : {
        req: trend,
        charts: trendCharts,
        axis: trendAxis,
        formatLabel: monthTick,
        formatPoint: monthFull,
        span: `last ${trendMeta.months || TREND_MONTHS} months`,
        noun: "month",
        adjective: "monthly",
      };

  // Data transfer. The rows arrive per (month, SKU, direction, unit) and the page
  // aggregates two ways, because they answer different questions:
  //
  //   In / out — how much moved each way. One chart per unit, one line per direction:
  //     unit is to a quantity what currency is to money, and GB shares no scale with
  //     TB, while inbound and outbound do share one and belong on the same axis.
  //   By SKU  — what kind of traffic it was. OCI meters egress per destination zone
  //     and per service, so an outbound total is several SKUs deep. Stacked within a
  //     direction, never across one: outbound and inbound bytes summed together total
  //     to a figure nothing spends.
  const transferRows = transfer.data?.data || [];
  const transferMeta = transfer.data?.meta || {};
  const transferAxis = transferMeta.axis || [];
  const transferByUnit = new Map();
  for (const r of transferRows) {
    const byDirection = transferByUnit.get(r.unit) || new Map();
    const months = byDirection.get(r.direction) || new Map();
    months.set(r.month, (months.get(r.month) || 0) + (r.quantity || 0));
    byDirection.set(r.direction, months);
    transferByUnit.set(r.unit, byDirection);
  }
  const transferUnits = transferMeta.units || [...transferByUnit.keys()];
  const transferCharts = transferUnits.map((unit) => {
    const byDirection = transferByUnit.get(unit) || new Map();
    return {
      unit,
      // Fixed order, not by size: inbound and outbound are two flows, and which is
      // larger is the reader's question rather than the axis's.
      series: ["inbound", "outbound"]
        .filter((d) => byDirection.has(d))
        .map((d) => {
          const months = byDirection.get(d);
          return {
            key: d,
            label: titleCase(d),
            // A month with nothing metered is a gap, for the reason it is on the
            // spend chart: OCI reporting nothing is not OCI reporting zero.
            values: transferAxis.map((m) =>
              months.has(m) ? months.get(m) : null,
            ),
            total: [...months.values()].reduce((sum, v) => sum + v, 0),
          };
        }),
    };
  });

  // The per-SKU split: one chart per (unit, direction), SKUs stacked inside it. The
  // pair is the split key precisely because it is what may share an axis — the same
  // fold, ranking, and gap handling the spend charts use.
  const transferSkuCharts = monthlyCharts(transferRows, {
    axis: transferAxis,
    splitBy: (r) => `${r.unit}|${r.direction}`,
    groupBy: (r) => r.skuId,
    labelOf: (r) => r.skuName || r.skuId,
    valueOf: (r) => r.quantity,
    groupNoun: "SKU",
  }).map((chart) => {
    const [unit, direction] = String(chart.currency).split("|");
    return { ...chart, unit, direction };
  });
  const hasInbound = transferCharts.some((c) =>
    c.series.some((s) => s.key === "inbound"),
  );
  // The SKUs the series were read from. Shown because the direction is a name match:
  // one line and no explanation reads as "this tenancy only sends", when it may mean
  // "no SKU here said inbound" — and only the second is something anyone can act on.
  const transferSkus = transferMeta.skus || [];
  const undirectedSkus = transferSkus.filter((s) => !s.direction);

  // The date × service · SKU pivot. Cost, Billing, and Usage are three tabs
  // over two different tables: the money ones split their columns by currency,
  // Usage splits by unit and carries no currency at all. lib/pivot builds both
  // models and hands back one shape, so nothing below branches on the metric.
  const pivot = buildPivot(rawRows, { fallbackCurrency: usageCurrency });
  const pivotDates = pivot.dates;
  // Billing falls back to cost when the current report has no priced rows —
  // otherwise switching group or date range could leave the table showing an
  // all-zero billing column.
  const metricShown = metric === "billing" && !hasBilling ? "cost" : metric;
  const view = pivot.viewFor(metricShown);

  // Each metric exports its own file, matching the table that tab would show.
  const exportCsv = (m) =>
    downloadCsv(
      pivotCsv(pivot.viewFor(m), pivotDates),
      `usage-${m}-${applied?.provider || "oci"}-${applied?.start || "start"}_${applied?.end || "end"}.csv`,
    );

  return (
    <div className="stack">
      <PageHeader
        title="Cost & Billing"
        subtitle={`Spend analysis · ${scopeName}`}
      />

      {/* Filters (search bar) — above the usage report */}
      <Card title="Usage filters">
        <div className="filter-row">
          <label className="field">
            <span>Provider</span>
            <select
              className="input"
              value={usageProvider}
              onChange={(e) => setUsageProvider(e.target.value)}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.shortName}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>User group</span>
            <select
              className="input"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">All tenants</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.tenancyName ? ` · ${g.tenancyName}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Start date</span>
            <input
              className="input"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label className="field">
            <span>End date</span>
            <input
              className="input"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Filter</span>
            <input
              className="input"
              placeholder="tenant, service, or SKU…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className="field">
            <span aria-hidden="true">&nbsp;</span>
            <button
              className="btn btn--primary"
              onClick={applyUsage}
              disabled={usage.loading}
            >
              {usage.loading ? "Loading…" : "Get usage"}
            </button>
          </div>
        </div>
      </Card>

      {/* Summary box — above the usage report */}
      <Card
        title="Summary"
        action={
          hasSearched && !usage.loading && !usage.error ? (
            <div className="usage-export">
              <button
                className="btn btn--primary btn--sm"
                onClick={storeUsage}
                disabled={syncing || !applied?.groupId || rawRows.length === 0}
                title={
                  !applied?.groupId
                    ? "Select a user group — stored billing is per group"
                    : "Store this window so the group can read it on Billing"
                }
              >
                {syncing ? "Storing…" : "⭱ Store for client"}
              </button>
            </div>
          ) : null
        }
      >
        {!hasSearched ? (
          <p className="listview__desc">
            Choose filters and click <strong>Get usage</strong> to load the
            report.
          </p>
        ) : usage.loading ? (
          <Loading label="Loading usage…" />
        ) : usage.error ? (
          <ErrorState error={usage.error} onRetry={usage.reload} />
        ) : (
          <>
            {/* The summary reads one concern per row: what the report covers, then
                what moved, then the money — one row per currency, since figures in
                different currencies are never added together or compared. */}
            <div className="grid grid--kpi usage-summary">
              <StatCard
                label="Tenants"
                value={number(tenantCount)}
                hint={
                  applied?.tenant
                    ? `tenant ${applied.tenantLabel || applied.tenant}`
                    : "all tenants"
                }
              />
              <StatCard label="Services" value={number(serviceCount)} />
              <StatCard label="Line items" value={number(filtered.length)} />
            </div>

            {/* Data transfer. Its own row because it is measured, not charged: the
                figures are quantities in the provider's own units, and they sit
                above the money rather than among it. */}
            <div className="grid grid--kpi usage-summary">
              <StatCard
                label="Inbound bandwidth"
                value={bandwidthText(bandwidth.inbound, qty)}
                hint={
                  bandwidth.inboundLines > 0
                    ? `${number(bandwidth.inboundLines)} data-transfer line${
                        bandwidth.inboundLines === 1 ? "" : "s"
                      }`
                    : // Absence, not zero: this report is a cost query and OCI does
                      // not charge for ingress, so there is usually no inbound line in
                      // it at all. The Data transfer card below runs a metered query,
                      // which is the one that can see it.
                      "not charged — see Data transfer"
                }
              />
              <StatCard
                label="Outbound bandwidth"
                value={bandwidthText(bandwidth.outbound, qty)}
                hint={
                  bandwidth.outboundLines > 0
                    ? `${number(bandwidth.outboundLines)} data-transfer line${
                        bandwidth.outboundLines === 1 ? "" : "s"
                      }`
                    : "no data-transfer line in this window"
                }
              />
            </div>

            {/* Cost, billing, and margin read across one line, and the line is one
                currency: the three figures only mean anything together — margin is
                the gap between the other two — and they are only comparable within a
                currency. Two currencies are therefore two rows, never two columns of
                one row, so nothing on a line can be mistaken for a running total of
                the line above it. */}
            {summaryCurrencies.map((c) => (
              <div className="grid grid--kpi usage-summary" key={`money-${c}`}>
                <StatCard
                  label={perCurrency("Usage cost", c)}
                  value={currencyExact(totalsFor(c).cost, c)}
                  hint="what OCI charged"
                />
                {hasBilling ? (
                  <>
                    <StatCard
                      label={perCurrency("Billing", c)}
                      value={currencyExact(totalsFor(c).billing, c)}
                      hint="quantity × OCI public price"
                    />
                    <StatCard
                      label={perCurrency("Margin", c)}
                      value={marginPct(c)}
                      hint="billing minus OCI cost"
                    />
                  </>
                ) : null}
              </div>
            ))}

            {contracts.length > 0 ? (
              <div className="grid grid--kpi usage-summary">
                <StatCard
                  label="Contract value"
                  value={currencyExact(contractValue, contractCurrency)}
                  hint={`${contracts.length} contract${contracts.length === 1 ? "" : "s"}`}
                />
                <StatCard
                  label="% used"
                  value={pctUsed != null ? `${pctUsed.toFixed(1)}%` : "—"}
                  hint={
                    multiCurrency
                      ? `of contract value · ${contractCurrency} usage only`
                      : "of contract value"
                  }
                />
                <StatCard
                  label="Remainder"
                  value={currencyExact(remainder, contractCurrency)}
                  hint={remainder < 0 ? "over contract" : "left"}
                />
              </div>
            ) : null}

            {usageMeta.fetchedAt ? (
              <p className="field__hint">
                Read from {titleCase(applied?.provider || "oci")} at{" "}
                <strong>
                  {new Date(usageMeta.fetchedAt).toLocaleString()}
                </strong>
                {applied?.end === todayUTC()
                  ? " — today is still in progress, so its figures will keep rising."
                  : "."}
              </p>
            ) : null}

            {syncError ? <div className="form-error">{syncError}</div> : null}
            {syncResult ? (
              <p className="field__hint">
                Stored <strong>{number(syncResult.rows)}</strong> line
                {syncResult.rows === 1 ? "" : "s"} for{" "}
                <strong>{syncResult.groupName}</strong> ({syncResult.start} →{" "}
                {syncResult.end}), billing{" "}
                {currencyExact(syncResult.billing, syncResult.currency)}
                {syncResult.replaced > 0
                  ? ` · replaced ${number(syncResult.replaced)} previously stored line${
                      syncResult.replaced === 1 ? "" : "s"
                    }`
                  : ""}
                . The group can now see this on Billing.
              </p>
            ) : null}

            {contracts.length > 0 && (
              <div className="contract-summary">
                {contracts.map((c) => (
                  <span key={c.id} className="chip">
                    <strong>{c.type}</strong>:{" "}
                    {currencyExact(c.value, c.currency)}
                    <span className="chip__sub">
                      {fmtDate(c.start)}–{fmtDate(c.end)}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Spend over time, from its own queries — MONTHLY over the last year, or DAILY
          over the chosen window — never a re-aggregation of the report above. The
          report is grouped to the SKU, so it would cost (days × services × SKUs) rows
          to draw (days × services) points, and a year of it tens of thousands to draw
          twelve. */}
      <Card
        className="card--quiet-title"
        // The heading names what is on screen, the way the pivot's does below: the
        // views show different things, not one thing relabelled — and the grain is
        // part of that, since "Monthly" and "Daily" are different claims about every
        // column.
        title={`${titleCase(spend.adjective)} spend${
          trendView === "total" ? "" : " by service"
        }${spend.span ? ` — ${spend.span}` : ""}`}
        action={
          <div className="usage-export">
            <div
              className="seg"
              role="tablist"
              aria-label="How finely the spend is bucketed"
            >
              {[
                ["month", "Month"],
                ["day", "Day"],
              ].map(([grain, label]) => (
                <button
                  key={grain}
                  type="button"
                  role="tab"
                  className={`seg__btn ${trendGrain === grain ? "seg__btn--on" : ""}`}
                  onClick={() => setTrendGrain(grain)}
                  aria-selected={trendGrain === grain}
                  title={
                    grain === "month"
                      ? "Calendar months over the last year"
                      : "One column per day of the selected window"
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              className="seg"
              role="tablist"
              aria-label="How the spend is shown"
            >
              {[
                ["service", "By service"],
                ["total", "Total"],
                ["table", "Table"],
              ].map(([view, label]) => (
                <button
                  key={view}
                  type="button"
                  role="tab"
                  className={`seg__btn ${trendView === view ? "seg__btn--on" : ""}`}
                  onClick={() => setTrendView(view)}
                  aria-selected={trendView === view}
                  title={
                    view === "total"
                      ? `The ${spend.noun} totals as a line: is spend rising?`
                      : view === "service"
                        ? `Stacked columns: what each ${spend.noun} is made of`
                        : `Every service and ${spend.noun} as figures`
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Stays visible at either grain: it is labelled with its own span, and
                hiding a superadmin write behind a view toggle would be worse than
                showing it under a chart of days. The daily rows a client sees come
                from "Store for client" on the report above, not from here. */}
            {hasSearched && !spend.req.loading && !spend.req.error ? (
              <>
                {/* Beside the button rather than in the window form above: it scopes
                    the write, not the page, and putting it next to Get usage would
                    read as narrowing the report. */}
                {/* Clamped when the field is left, not as it is typed: the value
                    on the way out is always in range, and the button beside it
                    reads back what will actually be stored. */}
                <label
                  className="usage-export__span"
                  title={`How far back the client's stored spend chart reaches, ${MIN_SNAPSHOT_MONTHS}-${MAX_SNAPSHOT_MONTHS} months`}
                >
                  <input
                    className="input input--sm input--num"
                    type="number"
                    min={MIN_SNAPSHOT_MONTHS}
                    max={MAX_SNAPSHOT_MONTHS}
                    step="1"
                    value={snapshotMonths}
                    onChange={(e) => setSnapshotMonths(e.target.value)}
                    onBlur={() =>
                      setSnapshotMonths(String(snapshotSpan(snapshotMonths)))
                    }
                    disabled={monthsSyncing}
                    aria-label={`How many months to store for the client, ${MIN_SNAPSHOT_MONTHS} to ${MAX_SNAPSHOT_MONTHS}`}
                  />
                  <span>months</span>
                </label>
                <button
                  className="btn btn--primary btn--sm"
                  onClick={storeMonths}
                  disabled={monthsSyncing || !applied?.groupId}
                  title={
                    !applied?.groupId
                      ? "Select a user group — a stored snapshot is per group"
                      : `Store the last ${snapshotSpan(snapshotMonths)} calendar months so the group sees this chart on their bill. Replaces whatever is stored now.`
                  }
                >
                  {monthsSyncing
                    ? "Storing…"
                    : `⭱ Store ${snapshotSpan(snapshotMonths)} months for client`}
                </button>
              </>
            ) : null}
          </div>
        }
      >
        {!hasSearched ? (
          <p className="listview__desc">
            Click <strong>Get usage</strong> to load{" "}
            {byDay
              ? "spend per day over the window"
              : "the last year of monthly spend"}
            .
          </p>
        ) : spend.req.loading ? (
          <Loading label={`Loading ${spend.adjective} spend…`} />
        ) : spend.req.error ? (
          <ErrorState error={spend.req.error} onRetry={spend.req.reload} />
        ) : spend.charts.length === 0 ? (
          <p className="listview__desc">
            No {spend.adjective} cost reported
            {byDay ? " for this window" : " for this tenancy"}. (Only live
            providers such as OCI return a spend history.)
          </p>
        ) : trendView === "table" ? (
          <div className="chart-multiples">
            {spend.charts.map((c) => (
              <div key={c.currency}>
                {spend.charts.length > 1 ? (
                  <h3 className="chart-multiples__title">{c.label}</h3>
                ) : null}
                <div className="table-wrap">
                  {/* Service columns, ordered by what each one costs over the whole
                      span, with the bucket total last. Every service is here — the
                      chart's top-four fold is a limit of colour, not of the data. */}
                  <table className="table table--dense table--pivot">
                    <thead>
                      <tr>
                        <th>{byDay ? "Day" : "Month"}</th>
                        {c.services.map((s) => (
                          <th key={s.key} className="num">
                            {s.label}
                          </th>
                        ))}
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spend.axis.map((m, i) => (
                        <tr key={m}>
                          <td className="cell-primary">
                            {spend.formatPoint(m)}
                          </td>
                          {c.services.map((s) => (
                            <td key={s.key} className="num">
                              {/* A dash, not a zero: nothing was reported here. */}
                              {s.values[i] == null
                                ? "—"
                                : currencyExact(s.values[i], c.code)}
                            </td>
                          ))}
                          <td className="num cell-total">
                            {c.total.values[i] == null
                              ? "—"
                              : currencyExact(c.total.values[i], c.code)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="cell-primary">Total</td>
                        {c.services.map((s) => (
                          <td key={s.key} className="num cell-total">
                            {currencyExact(s.total, c.code)}
                          </td>
                        ))}
                        <td className="num cell-total">
                          {currencyExact(c.total.total, c.code)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="chart-multiples">
            {spend.charts.map((c) => (
              <div key={c.currency}>
                {/* Named only when there is more than one to tell apart — the y
                    ticks already carry the currency. */}
                {spend.charts.length > 1 ? (
                  <h3 className="chart-multiples__title">{c.label}</h3>
                ) : null}
                {/* Two forms for two questions, never one chart doing both: the
                    breakdown stacks to each bucket's total, so the column carries
                    "how much" and the segments carry "of what". Drawing the total as
                    a fifth line beside its own parts would dwarf every one of
                    them. */}
                {trendView === "service" ? (
                  <StackedBars
                    labels={spend.axis}
                    series={c.chartServices}
                    height={280}
                    formatValue={(v) => currencyExact(v, c.code)}
                    formatTick={(v) => currencyCompact(v, c.code)}
                    formatLabel={spend.formatLabel}
                    formatPoint={spend.formatPoint}
                    ariaLabel={`${c.code} cost by service per ${spend.noun}, ${spend.span}`}
                  />
                ) : (
                  <LineChart
                    labels={spend.axis}
                    series={[c.total]}
                    height={260}
                    formatValue={(v) => currencyExact(v, c.code)}
                    formatTick={(v) => currencyCompact(v, c.code)}
                    formatLabel={spend.formatLabel}
                    formatPoint={spend.formatPoint}
                    ariaLabel={`${c.code} cost per ${spend.noun}, ${spend.span}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {hasSearched &&
        !spend.req.loading &&
        !spend.req.error &&
        spend.charts.length > 0 ? (
          <p className="field__hint">
            What {titleCase(applied?.provider || "oci")} charged
            {applied?.tenantLabel
              ? ` for ${applied.tenantLabel}`
              : " across all tenancies"}
            , per calendar {spend.noun}. The last {spend.noun} is still
            accumulating
            {byDay ? " if the window reaches today" : ""}, and a {spend.noun}{" "}
            with nothing reported is left as a gap rather than drawn as zero.
            {byDay
              ? " These are the dates selected above — switch to Month for the year of context behind them."
              : ""}
            {/* The axis stops short of the picked window, and a chart that quietly
                did that would read as missing data rather than as a cap. */}
            {byDay && dailyMeta.truncated
              ? ` Only the first ${spend.axis.length} days of the window are charted.`
              : ""}
            {trendView === "service"
              ? " Only the four costliest services get a colour of their own — the rest are folded into Other, and Table lists every one."
              : ""}
          </p>
        ) : null}

        {monthsError ? <div className="form-error">{monthsError}</div> : null}
        {/* The span comes off the result, never off the picker: the operator may
            have moved the select since, and this line reports what was written. */}
        {monthsResult ? (
          <p className="field__hint">
            Stored <strong>{number(monthsResult.rows)}</strong> monthly line
            {monthsResult.rows === 1 ? "" : "s"} for{" "}
            <strong>{monthsResult.groupName}</strong> ({monthsResult.start} →{" "}
            {monthsResult.end}), billing{" "}
            {currencyExact(monthsResult.billing, monthsResult.currency)}
            {monthsResult.replaced > 0
              ? ` · replaced ${number(monthsResult.replaced)} previously stored line${
                  monthsResult.replaced === 1 ? "" : "s"
                }`
              : ""}
            . The group now sees that span on Billing, priced at their current
            rates — a month inside it that reported nothing draws as a gap.
          </p>
        ) : null}
      </Card>

      {/* Data transfer. Its own endpoint, and it has to be: everything else on this
          page is a cost query, and OCI does not charge for ingress — inbound traffic
          appears in no cost response at any granularity, so it can only come from a
          metered-usage query. */}
      <Card
        className="card--quiet-title"
        title={`Data transfer — last ${transferMeta.months || TREND_MONTHS} months`}
        action={
          <div
            className="seg"
            role="tablist"
            aria-label="How data transfer is shown"
          >
            {[
              ["flow", "In / out"],
              ["sku", "By SKU"],
              ["table", "Table"],
            ].map(([view, label]) => (
              <button
                key={view}
                type="button"
                role="tab"
                className={`seg__btn ${transferView === view ? "seg__btn--on" : ""}`}
                onClick={() => setTransferView(view)}
                aria-selected={transferView === view}
                title={
                  view === "flow"
                    ? "How much moved each way"
                    : view === "sku"
                      ? "What kind of traffic: OCI meters egress per zone and service"
                      : "Every SKU and month as figures"
                }
              >
                {label}
              </button>
            ))}
          </div>
        }
      >
        {!hasSearched ? (
          <p className="listview__desc">
            Click <strong>Get usage</strong> to load metered data transfer.
          </p>
        ) : transfer.loading ? (
          <Loading label="Loading data transfer…" />
        ) : transfer.error ? (
          <ErrorState error={transfer.error} onRetry={transfer.reload} />
        ) : transferCharts.length === 0 ? (
          <>
            <p className="listview__desc">
              No data-transfer usage reported for this tenancy over these
              months.
            </p>
            {undirectedSkus.length > 0 ? (
              // The one case worth spelling out: transfer *was* metered, but nothing in
              // the SKU names said which way it went, so no series could be built.
              <p className="field__hint">
                {undirectedSkus.length} transfer SKU
                {undirectedSkus.length === 1 ? "" : "s"} came back with no
                direction in the name, so{" "}
                {undirectedSkus.length === 1 ? "it" : "they"} could not be split
                into inbound and outbound:{" "}
                <strong>
                  {undirectedSkus
                    .map((s) => `${s.skuId} ${s.skuName} (${s.unit})`)
                    .join(", ")}
                </strong>
                .
              </p>
            ) : null}
          </>
        ) : transferView === "table" ? (
          <div className="chart-multiples">
            {/* Per SKU, not per direction: the table is where the grain the API
                actually carries stays readable, and the direction rides each column
                header rather than collapsing the SKUs behind it. */}
            {transferSkuCharts.map((c) => (
              <div key={c.currency}>
                <h3 className="chart-multiples__title">
                  {titleCase(c.direction)} · {c.unit}
                </h3>
                <div className="table-wrap">
                  <table className="table table--dense table--pivot">
                    <thead>
                      <tr>
                        <th>Month</th>
                        {c.services.map((s) => (
                          <th key={s.key} className="num">
                            {s.label}
                          </th>
                        ))}
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferAxis.map((m, i) => (
                        <tr key={m}>
                          <td className="cell-primary">{monthFull(m)}</td>
                          {c.services.map((s) => (
                            <td key={s.key} className="num">
                              {s.values[i] == null
                                ? "—"
                                : `${qty(s.values[i])} ${c.unit}`}
                            </td>
                          ))}
                          <td className="num cell-total">
                            {c.total.values[i] == null
                              ? "—"
                              : `${qty(c.total.values[i])} ${c.unit}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="cell-primary">Total</td>
                        {c.services.map((s) => (
                          <td key={s.key} className="num cell-total">
                            {qty(s.total)} {c.unit}
                          </td>
                        ))}
                        <td className="num cell-total">
                          {qty(c.total.total)} {c.unit}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : transferView === "sku" ? (
          <div className="chart-multiples">
            {/* One chart per (direction, unit), SKUs stacked within it. Stacking is
                only honest inside a direction: the segments of a column then sum to
                that direction's real total, whereas inbound stacked onto outbound
                would total to a figure that means nothing. */}
            {transferSkuCharts.map((c) => (
              <div key={c.currency}>
                <h3 className="chart-multiples__title">
                  {titleCase(c.direction)} · {c.unit}
                </h3>
                <StackedBars
                  labels={transferAxis}
                  series={c.chartServices}
                  height={280}
                  formatValue={(v) => `${qty(v)} ${c.unit}`}
                  formatTick={(v) => qty(v)}
                  formatLabel={monthTick}
                  formatPoint={monthFull}
                  ariaLabel={`Monthly ${c.direction} data transfer by SKU, in ${c.unit}`}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="chart-multiples">
            {transferCharts.map((c) => (
              <div key={c.unit}>
                {/* The unit names the chart: it is the scale, not a series label. */}
                <h3 className="chart-multiples__title">{c.unit}</h3>
                <LineChart
                  labels={transferAxis}
                  series={c.series}
                  height={260}
                  formatValue={(v) => `${qty(v)} ${c.unit}`}
                  formatTick={(v) => qty(v)}
                  formatLabel={monthTick}
                  formatPoint={monthFull}
                  ariaLabel={`Monthly data transfer in ${c.unit}, inbound and outbound`}
                />
              </div>
            ))}
          </div>
        )}
        {hasSearched &&
        !transfer.loading &&
        !transfer.error &&
        transferCharts.length > 0 ? (
          <>
            <p className="field__hint">
              Metered transfer — what moved, whether or not it was charged for,
              which is why this is a separate read from the cost figures above.
              {hasInbound
                ? ""
                : " No SKU here was named as inbound: OCI does not bill ingress and does not always meter it either, so this tenancy reports outbound only."}
              {transferView === "sku"
                ? " Only the four largest SKUs get a colour of their own — the rest are folded into Other, and Table lists every one."
                : ""}
            </p>
            {/* Provenance, because the split comes from reading SKU names. A reader who
                doubts a missing series can check the names against it. */}
            {transferSkus.length > 0 ? (
              <p className="field__hint">
                Read from{" "}
                {transferSkus
                  .map(
                    (s) =>
                      `${s.skuId} ${s.skuName} (${s.unit}${
                        s.direction
                          ? `, ${s.direction}`
                          : ", no direction in name"
                      })`,
                  )
                  .join(" · ")}
                .
              </p>
            ) : null}
          </>
        ) : null}
      </Card>

      {/* Usage report table */}
      <Card
        className="card--quiet-title"
        title="Usage report — by tenant · service · SKU"
      >
        {!hasSearched ? (
          <p className="listview__desc">
            Click <strong>Get usage</strong> to load the report.
          </p>
        ) : usage.loading ? (
          <Loading label="Loading usage…" />
        ) : usage.error ? (
          <ErrorState error={usage.error} onRetry={usage.reload} />
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Service</th>
                  <th>SKU ID</th>
                  <th>SKU name</th>
                  <th>Currency</th>
                  <th className="num">Quantity</th>
                  <th className="num" title="Cost divided by quantity">
                    List rate
                  </th>
                  <th className="num">Cost</th>
                  {hasBilling ? (
                    <th className="num" title="OCI public price for this SKU">
                      Billing rate
                    </th>
                  ) : null}
                  {hasBilling ? <th className="num">Billing</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={hasBilling ? 10 : 8} className="table__empty">
                      No usage to display
                    </td>
                  </tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr
                      key={`${r.tenantId || r.tenantName}-${r.skuId}-${r.currency}-${i}`}
                    >
                      <td
                        className="cell-primary"
                        title={r.tenantId || undefined}
                      >
                        {r.tenantName || tenantTail(r.tenantId)}
                      </td>
                      <td>{r.service || "—"}</td>
                      <td className="cell-type">{r.skuId || "—"}</td>
                      <td>
                        {r.skuName || "—"}
                        {/* Which lines the bandwidth cards above are counting. The
                            backend derives the direction from the SKU's name, so
                            showing it here is what makes that reading checkable
                            rather than asserted. */}
                        {r.direction ? (
                          <span
                            className="badge badge--muted table__tag"
                            title="Counted as data transfer, by SKU name"
                          >
                            {r.direction === "inbound" ? "in" : "out"}
                          </span>
                        ) : null}
                      </td>
                      <td>{r.currency || "—"}</td>
                      <td className="num">
                        {qty(r.quantity)}
                        {r.unit ? ` ${r.unit}` : ""}
                      </td>
                      <td className="num" title="Cost divided by quantity">
                        {r.quantity > 0 ? rateText(r.cost / r.quantity) : "—"}
                      </td>
                      <td className="num">
                        {currencyExact(r.cost, r.currency || usageCurrency)}
                      </td>
                      {hasBilling ? (
                        <td
                          className="num"
                          title="OCI public price for this SKU"
                        >
                          {rateText(r.billingPrice)}
                        </td>
                      ) : null}
                      {hasBilling ? (
                        <td className="num cell-total">
                          {currencyExact(
                            r.billing,
                            r.currency || usageCurrency,
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Usage by date × service · SKU (with export) */}
      <Card
        className="card--quiet-title"
        // The heading names the table on screen: the tabs show different things,
        // not the same table relabelled.
        title={`${titleCase(metricShown)} by date × service · SKU`}
        action={
          <div className="usage-export">
            <div
              className="seg"
              role="tablist"
              aria-label="What the table shows"
            >
              {["cost", "billing", "usage"].map((m) => (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  className={`seg__btn ${metricShown === m ? "seg__btn--on" : ""}`}
                  onClick={() => setMetric(m)}
                  aria-selected={metricShown === m}
                  // Billing follows OCI cost when a group is selected.
                  disabled={m === "billing" && !hasBilling}
                  title={
                    m === "billing" && !hasBilling
                      ? "Select a group to show billing cost"
                      : m === "usage"
                        ? "Quantities per day, split by unit"
                        : `${titleCase(m)} per day, split by currency`
                  }
                >
                  {titleCase(m)}
                </button>
              ))}
            </div>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => exportCsv("cost")}
              disabled={rawRows.length === 0}
              title="Download the cost table as CSV (';' separated, 2 decimals)"
            >
              ⭳ Cost CSV
            </button>
            {hasBilling ? (
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => exportCsv("billing")}
                disabled={rawRows.length === 0}
                title="Download the billing table as CSV (';' separated, 2 decimals)"
              >
                ⭳ Billing CSV
              </button>
            ) : null}
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => exportCsv("usage")}
              disabled={rawRows.length === 0}
              title="Download the usage table as CSV (';' separated)"
            >
              ⭳ Usage CSV
            </button>
          </div>
        }
      >
        {!hasSearched ? (
          <p className="listview__desc">
            Click <strong>Get usage</strong> to load the report.
          </p>
        ) : usage.loading ? (
          <Loading label="Loading usage…" />
        ) : usage.error ? (
          <ErrorState error={usage.error} onRetry={usage.reload} />
        ) : pivotDates.length === 0 ? (
          <p className="listview__desc">
            No dated usage for this provider/range. (Only live providers such as
            OCI return per-date cost.)
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table table--dense table--pivot">
              <thead>
                <tr>
                  <th>Date</th>
                  {view.cols.map((key) => (
                    <th key={key} className="num">
                      {view.label(key)}
                    </th>
                  ))}
                  {view.rowTotals
                    ? view.totalKeys.map((c) => (
                        <th key={c} className="num">
                          {view.totalLabel(c)}
                        </th>
                      ))
                    : null}
                </tr>
              </thead>
              <tbody>
                {pivotDates.map((d) => (
                  <tr key={d}>
                    <td className="cell-primary">{d}</td>
                    {view.cols.map((key) => (
                      <td key={key} className="num">
                        {view.value(d, key)
                          ? view.text(view.value(d, key), key)
                          : "—"}
                      </td>
                    ))}
                    {view.rowTotals
                      ? view.totalKeys.map((c) => (
                          <td key={c} className="num cell-total">
                            {view.totalText(view.rowTotal(d, c), c)}
                          </td>
                        ))
                      : null}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="cell-primary">Total</td>
                  {view.cols.map((key) => (
                    <td key={key} className="num cell-total">
                      {view.text(view.columnTotal(key), key)}
                    </td>
                  ))}
                  {view.rowTotals
                    ? view.totalKeys.map((c) => (
                        <td key={c} className="num cell-total">
                          {view.totalText(view.grandTotal(c), c)}
                        </td>
                      ))
                    : null}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
